import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, PanResponder } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from 'react-native-reanimated';
import { HierarchyPosition, HierarchyItem } from './BaseHierarchy';

// Arrow data structure for centralized rendering
export interface ArrowData {
  id: string;
  path: string;
  color: string;
  angle: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  reset?: boolean;
}

// User-specific interfaces
interface UserProfile {
  name?: string;
  surname?: string;
  avatar?: any;
}

interface User extends HierarchyItem {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
  department?: string;
  role?: string;
  status?: string;
}

export interface DraggableUserCardHandle {
  setPosition: (position: HierarchyPosition) => void;
  setPositionSilent: (position: HierarchyPosition) => void; // Update position without calling onPositionChange (for department drag)
  getPosition: () => HierarchyPosition;
  setArrowTranslate: (path: string, color: string) => void;
  disableComponent: () => void;
  enableComponent: () => void;
}

interface DraggableUserCardProps {
  user: User;
  initialPosition: HierarchyPosition;
  onPositionChange: (itemId: string, newPosition: HierarchyPosition) => void;
  onPositionUpdate?: (itemId: string, newPosition: HierarchyPosition) => void;
  onSnap?: (target: string, source: string) => void;
  getAllUserPositions?: () => Record<string, HierarchyPosition>;
  arrowHandler?: (data: ArrowData) => void;
  handleComponentArrowTranslation?: (event: any) => void;
  zoomProps?: { zoomLevel: number; offsetX: number; offsetY: number };
  dragEnabled?: boolean;
  zoomLevel?: number;
  itemWidth?: number;
  itemHeight?: number;
  borderRadius?: number;
  shadowEnabled?: boolean;
  onPress?: (user: User) => void; // Click handler for editing
  children: (props: { isDragging: boolean }) => React.ReactNode;
}

const DraggableUserCard = forwardRef<DraggableUserCardHandle, DraggableUserCardProps>((props, ref) => {
  const {
    user,
    initialPosition,
    onPositionChange,
    onPositionUpdate,
    onSnap,
    getAllUserPositions,
    arrowHandler,
    handleComponentArrowTranslation,
    zoomProps,
    dragEnabled = true,
    zoomLevel = 1,
    itemWidth = 200,
    itemHeight = 120,
    borderRadius = 12,
    shadowEnabled = true,
    onPress,
    children
  } = props;

  // Position management (from DraggableHierarchyItem)
  const translationX = useSharedValue(initialPosition.x);
  const translationY = useSharedValue(initialPosition.y);
  const isDraggingShared = useSharedValue(false);
  const startPosition = useRef({ x: initialPosition.x, y: initialPosition.y });
  const [isDraggingState, setIsDraggingState] = useState(false);

  // Arrow functionality (from original DraggableUserCard)
  const absoluteX = useSharedValue(0);
  const absoluteY = useSharedValue(0);
  const snapThreshold = 150;
  const [hideArrows, setHideArrows] = useState(false);
  const isArrowRef = useRef(false);
  const lastSnapTargetRef = useRef<string | null>(null); // Store user ID we snapped to - persists across gesture events!

  const ctx = useSharedValue({
    startX: 0,
    startY: 0,
    startAbsoluteX: 0,
    startAbsoluteY: 0,
    isSnapped: false,
    snappedIndex: -1
  });

  // Convert gesture values to view coordinates
  const convertGestureToViewCoordinates = useCallback(({
    absoluteX,
    absoluteY,
    translationX,
    translationY,
    parentOffset,
    zoomLevel,
  }) => {
    const adjustedX = (absoluteX - parentOffset.x) / zoomLevel;
    const adjustedY = (absoluteY - parentOffset.y) / zoomLevel;

    const translatedViewX = adjustedX + translationX / zoomLevel;
    const translatedViewY = adjustedY + translationY / zoomLevel;

    return { viewX: translatedViewX, viewY: translatedViewY };
  }, []);

  // Imperative handle
  useImperativeHandle(ref, () => ({
    setPosition: (position: HierarchyPosition) => {
      translationX.value = position.x;
      translationY.value = position.y;
      onPositionChange(user._id, position);
    },
    setPositionSilent: (position: HierarchyPosition) => {
      // Update position WITHOUT calling onPositionChange (for department drag)
      // This prevents state updates that interrupt drag gestures
      translationX.value = position.x;
      translationY.value = position.y;
      // NO onPositionChange call - this is the key difference
    },
    getPosition: () => ({
      x: translationX.value,
      y: translationY.value
    }),
    setArrowTranslate: (path: string, color: string) => {
      // No longer needed - arrows handled by parent renderer
      // Kept for backward compatibility
    },
    disableComponent: () => {
      setHideArrows(true);
    },
    enableComponent: () => {
      setHideArrows(false);
    },
  }), [user._id, translationX, translationY, onPositionChange]);

  // Arrow PanResponder (for connection drawing)
  const arrowPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDraggingState,
    onPanResponderGrant: (event, gestureState) => {
      const { locationX: viewX, locationY: viewY } = event.nativeEvent;
      const currentZoomLevel = zoomProps?.zoomLevel || zoomLevel || 1;

      ctx.value.startX = 0; // Start from center of the card
      ctx.value.startY = 0; // Start from center of the card  
      ctx.value.startAbsoluteX = absoluteX.value;
      ctx.value.startAbsoluteY = absoluteY.value;
      ctx.value.isSnapped = false;
      ctx.value.snappedIndex = -1;

      isArrowRef.current = true;
    },
    onPanResponderMove: (event, gestureState) => {
      const {
        dx: tX,
        dy: tY,
        moveX: ax,
        moveY: ay,
      } = gestureState;
      const currentZoomProps = zoomProps || { offsetX: 0, offsetY: 0, zoomLevel: zoomLevel || 1 };
      const { offsetX, offsetY, zoomLevel: currentZoomLevel } = currentZoomProps;

      // Update absolute values for gesture tracking
      absoluteX.value = ax;
      absoluteY.value = ay;

      // DON'T reset snappedIndex here - let the sticky snap logic manage it!
      // ctx.value.snappedIndex = -1; // REMOVED - this was wiping out snap state every frame!

      const { viewX, viewY } = convertGestureToViewCoordinates({
        absoluteX: ax,
        absoluteY: ay,
        translationX: tX,
        translationY: tY,
        parentOffset: { x: offsetX, y: offsetY },
        zoomLevel: currentZoomLevel,
      });

      // Forward updated coordinates to parent
      if (handleComponentArrowTranslation) {
        const modifiedEvent = { ...event, viewX, viewY };
        handleComponentArrowTranslation(modifiedEvent);
      }

      // Get all user positions for arrow detection
      const allUserPositions = getAllUserPositions ? getAllUserPositions() : {};
      
      // Get current position - prefer from getAllUserPositions, fallback to props
      let currentUserPosition = allUserPositions[user._id];
      if (!currentUserPosition) {
        // Use initial position as fallback instead of reading shared value in PanResponder
        currentUserPosition = { x: initialPosition.x, y: initialPosition.y };
      }

      // If no positions available, skip snapping
      if (!allUserPositions || Object.keys(allUserPositions).length === 0) {
        // Just draw a free arrow
        const interpolatedX = tX / currentZoomLevel;
        const interpolatedY = tY / currentZoomLevel;
        const dragX = ctx.value.startX + interpolatedX;
        const dragY = ctx.value.startY + interpolatedY;

        const controlPoint1X = dragX * 0.5;
        const controlPoint1Y = dragY * 0.5;
        const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
        
        // Calculate card center for absolute positioning
        const cardCenterX = currentUserPosition.x + (itemWidth / 2);
        const cardCenterY = currentUserPosition.y + (itemHeight / 2);

        if (arrowHandler) {
          arrowHandler({
            id: user._id,
            path: pathValue,
            color: '#3b82f6',
            angle: Math.atan2(tY, tX) * (180 / Math.PI),
            startX: cardCenterX,
            startY: cardCenterY,
            endX: dragX,
            endY: dragY
          });
        }
        return;
      }

      // Calculate current drag position in world coordinates
      const interpolatedX = tX / currentZoomLevel;
      const interpolatedY = tY / currentZoomLevel;
      const dragX = ctx.value.startX + interpolatedX;
      const dragY = ctx.value.startY + interpolatedY;

      // Calculate current card center in world coordinates
      const currentCardCenterX = currentUserPosition.x + (itemWidth / 2);
      const currentCardCenterY = currentUserPosition.y + (itemHeight / 2);

      // Convert drag position to world coordinates (from card center)
      const worldDragX = currentCardCenterX + dragX;
      const worldDragY = currentCardCenterY + dragY;

      // Initialize based on current snap state (sticky snap logic)
      let snappedToUser = ctx.value.isSnapped;
      let breakFreeDistance = 250; // Distance to break free from snap

      // If already snapped, check if we should break free
      if (ctx.value.isSnapped && ctx.value.snappedIndex !== -1) {
        const snappedPosition = Object.values(allUserPositions)[ctx.value.snappedIndex];
        if (snappedPosition) {
          const snappedCenterX = snappedPosition.x + (itemWidth / 2);
          const snappedCenterY = snappedPosition.y + (itemHeight / 2);
          const distanceToSnapped = Math.sqrt(
            Math.pow(snappedCenterX - worldDragX, 2) + Math.pow(snappedCenterY - worldDragY, 2)
          );

          // Stay snapped if still within break-free distance
          if (distanceToSnapped < breakFreeDistance) {
            snappedToUser = true;
            // Maintain current snap
            const relativeTargetX = snappedCenterX - currentCardCenterX;
            const relativeTargetY = snappedCenterY - currentCardCenterY;

            const controlPoint1X = relativeTargetX * 0.3;
            const controlPoint1Y = relativeTargetY * 0.3;
            const controlPoint2X = relativeTargetX * 0.7;
            const controlPoint2Y = relativeTargetY * 0.7;

            const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;

            if (arrowHandler) {
              arrowHandler({
                id: user._id,
                path: arrowPathValue,
                color: '#22c55e',
                angle: 90,
                startX: currentCardCenterX,
                startY: currentCardCenterY,
                endX: snappedCenterX,
                endY: snappedCenterY,
                reset: false
              });
            }
          } else {
            // Break free - reset snap and clear ref
            ctx.value.isSnapped = false;
            ctx.value.snappedIndex = -1;
            snappedToUser = false; // Important: update local variable too!

            // Draw blue arrow when breaking free
            const controlPoint1X = dragX * 0.5;
            const controlPoint1Y = dragY * 0.5;
            const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;

            if (arrowHandler) {
              arrowHandler({
                id: user._id,
                path: pathValue,
                color: '#3b82f6',
                angle: Math.atan2(dragY, dragX) * (180 / Math.PI),
                startX: currentCardCenterX,
                startY: currentCardCenterY,
                endX: worldDragX,
                endY: worldDragY,
                reset: false
              });
            }

            lastSnapTargetRef.current = null;
          }
        }
      }

      // If not currently snapped, check for new snaps
      if (!snappedToUser) {
      Object.entries(allUserPositions).forEach(([userId, position], index) => {
        if (userId === user._id) return; // Skip self
        if (snappedToUser) return; // Already snapped to someone

        const targetX = position.x;
        const targetY = position.y;

        // Calculate target card center
        const targetCenterX = targetX + (itemWidth / 2);
        const targetCenterY = targetY + (itemHeight / 2);

        // Calculate distance from drag point to target center
        const distance = Math.sqrt(
          Math.pow(targetCenterX - worldDragX, 2) + Math.pow(targetCenterY - worldDragY, 2)
        );

        if (distance < snapThreshold) {
          // Calculate relative positions for arrow path from current card center to target center
          const relativeTargetX = targetCenterX - currentCardCenterX;
          const relativeTargetY = targetCenterY - currentCardCenterY;

          // Create smooth bezier curve
          const controlPoint1X = relativeTargetX * 0.3;
          const controlPoint1Y = relativeTargetY * 0.3;
          const controlPoint2X = relativeTargetX * 0.7;
          const controlPoint2Y = relativeTargetY * 0.7;

          const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;

          // Send arrow data to parent (PanResponder runs on JS thread, no runOnJS needed)
          if (arrowHandler) {
            arrowHandler({
              id: user._id,
              path: arrowPathValue,
              color: '#22c55e',
              angle: 90,
              startX: currentCardCenterX,
              startY: currentCardCenterY,
              endX: relativeTargetX,
              endY: relativeTargetY
            });
          }

          ctx.value.snappedIndex = index;
          ctx.value.isSnapped = true;
          snappedToUser = true;

          // Store the snap target in ref - this persists across gesture events!
          lastSnapTargetRef.current = userId;
        }
      });
      }

      // Handle unsnapped state - draw arrow following the drag
      if (!snappedToUser) {
        const controlPoint1X = dragX * 0.5;
        const controlPoint1Y = dragY * 0.5;

      const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
      const currentAngle = Math.atan2(tY, tX) * (180 / Math.PI);

      // Send arrow data to parent (PanResponder runs on JS thread, no runOnJS needed)
      if (arrowHandler) {
        arrowHandler({
          id: user._id,
          path: pathValue,
          color: '#3b82f6',
          angle: currentAngle,
          startX: currentCardCenterX,
          startY: currentCardCenterY,
          endX: worldDragX,
          endY: worldDragY,
          reset: false
        });
      }

      // SAFETY: If we're drawing a free (blue) arrow and ref is still set, clear it!
      // This handles cases where break-free logic didn't run (e.g., quick release)
      if (ctx.value.isSnapped) {
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;
      }
      if (lastSnapTargetRef.current !== null) {
        lastSnapTargetRef.current = null;
      }
      }
    },
    onPanResponderRelease: () => {
      // Read the snap target from ref - much more reliable!
      const snappedUserId = lastSnapTargetRef.current;

      // Reset arrow (PanResponder runs on JS thread, no runOnJS needed)
      if (arrowHandler) {
        arrowHandler({
          id: user._id,
          path: `M0,0 L0,0`,
          color: '#3b82f6',
          angle: 270,
          startX: 0,
          startY: 0,
          endX: 0,
          endY: 0,
          reset: true
        });
      }

      // Trigger callback if we snapped
      if (snappedUserId && onSnap) {
        onSnap(snappedUserId, user._id);
      }

      // Clear ref after handling
      lastSnapTargetRef.current = null;

      ctx.value.isSnapped = false;
      ctx.value.snappedIndex = -1;
      isArrowRef.current = false;
    },
  });

  // Drag PanResponder (for moving the card)
  // Track drag distance to distinguish clicks from drags
  const dragDistanceRef = useRef({ dx: 0, dy: 0 });
  const dragStartTimeRef = useRef(0);

  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDraggingState,
    onPanResponderGrant: (event, gestureState) => {
      setIsDraggingState(true);
      isDraggingShared.value = true;
      startPosition.current = { x: translationX.value, y: translationY.value };
      dragDistanceRef.current = { dx: 0, dy: 0 };
      dragStartTimeRef.current = Date.now();
    },
    onPanResponderMove: (event, gestureState) => {
      if (!isDraggingState) return;

      const { dx, dy } = gestureState;
      dragDistanceRef.current = { dx, dy };
      const newX = startPosition.current.x + dx / zoomLevel;
      const newY = startPosition.current.y + dy / zoomLevel;

      translationX.value = newX;
      translationY.value = newY;

      // Update position for real-time tracking
      if (onPositionUpdate) {
        onPositionUpdate(user._id, { x: newX, y: newY });
      }
    },
    onPanResponderRelease: (event, gestureState) => {
      setIsDraggingState(false);
      isDraggingShared.value = false;

      const { dx, dy } = dragDistanceRef.current;
      const dragDistance = Math.sqrt(dx * dx + dy * dy);
      const dragDuration = Date.now() - dragStartTimeRef.current;

      // If it was a click (small movement, short duration), trigger onPress
      if (onPress && dragDistance < 10 && dragDuration < 200) {
        onPress(user);
        return; // Don't update position for clicks
      }

      // Snap to grid for clean positioning
      const gridSize = 50;
      const snappedX = Math.round(translationX.value / gridSize) * gridSize;
      const snappedY = Math.round(translationY.value / gridSize) * gridSize;

      translationX.value = withSpring(snappedX);
      translationY.value = withSpring(snappedY);

      // Final position update
      const finalPosition = { x: snappedX, y: snappedY };
      onPositionChange(user._id, finalPosition);
    },
  });

  // Animated style for the draggable item
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: translationX.value },
        { translateY: translationY.value },
      ],
      zIndex: isDraggingShared.value ? 1000 : 1,
    };
  });

  return (
    <Animated.View style={[animatedStyle]}>
      <View
        style={[
          styles.container,
          {
            width: itemWidth,
            height: itemHeight,
            borderRadius,
            ...(shadowEnabled && styles.shadow),
          }
        ]}
      >
        {/* Main Card Content with Arrow PanResponder */}
        <View
          {...arrowPanResponder.panHandlers}
          style={[styles.cardContent, {userSelect: 'none'}]}
        >
          {children({ isDragging: isDraggingState })}
        </View>

        {/* Drag Handle - separate from main card */}
        <View
          {...dragPanResponder.panHandlers}
          style={styles.dragHandle}
        />

        {/* Arrow rendering is now handled by parent HierarchyArrowRenderer */}
      </View>
    </Animated.View>
  );
});

DraggableUserCard.displayName = 'DraggableUserCard';

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
  },
  cardContent: {
    flex: 1,
    overflow: 'hidden',
  },
  dragHandle: {
    position: 'absolute',
    right: -15,
    top: '50%',
    marginTop: -15,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    borderWidth: 2,
    borderColor: '#3b82f6',
    zIndex: 15,
  },
  shadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
});

export default DraggableUserCard;
